import { Routes, Route } from 'react-router-dom'
import { FeedbackProvider } from '@/components/ui/feedback'

// Layouts
import MainLayout from './layouts/MainLayout'

// Pages — statically imported (NOT React.lazy): the prerenderer renders through
// renderToString, which is synchronous and cannot await a lazy() chunk, so lazy
// routes would SSR the Suspense fallback instead of real content. Static imports
// keep every route crawlable. See docs/conventions/frontend.md + scripts/prerender-bespoke.mjs.
import HomePage from './pages/HomePage'
import AboutPage from './pages/AboutPage'
import ContactPage from './pages/ContactPage'
import GetStartedPage from './pages/GetStartedPage'
import BookPage from './pages/BookPage'
import WhoWeHelpPage from './pages/WhoWeHelpPage'
import HowWeMeasurePage from './pages/HowWeMeasurePage'
import PayPage from './pages/PayPage'
import LinkInBioPage from './pages/LinkInBioPage'
import ChecklistPage from './pages/ChecklistPage'
import RequestsPage from './pages/RequestsPage'
import WelcomePage from './pages/WelcomePage'
import CallPage from './pages/CallPage'

// Packages
import PackagesPage from './pages/packages/PackagesPage'

// Portfolio
import PortfolioPage from './pages/portfolio/PortfolioPage'
import PortfolioItemPage from './pages/portfolio/PortfolioItemPage'

// Blog
import BlogPage from './pages/blog/BlogPage'
import BlogPostPage from './pages/blog/BlogPostPage'
import ChromaFestivalCaseStudyPage from './pages/blog/ChromaFestivalCaseStudyPage'
import FestivalsLosingApplicantsPage from './pages/blog/FestivalsLosingApplicantsPage'
import SierraCanyonCaseStudyPage from './pages/blog/SierraCanyonCaseStudyPage'
import HolyTrinityCaseStudyPage from './pages/blog/HolyTrinityCaseStudyPage'
import BrassCityJazzCaseStudyPage from './pages/blog/BrassCityJazzCaseStudyPage'
import WendellWhalumCaseStudyPage from './pages/blog/WendellWhalumCaseStudyPage'
import BestWebsitePlatformsPage from './pages/blog/BestWebsitePlatformsPage'

// Services
import ServicesPage from './pages/services/ServicesPage'
import FestivalRetainerPage from './pages/services/FestivalRetainerPage'
import DigitalSystemsPage from './pages/services/DigitalSystemsPage'
import ApplicationFunnelsPage from './pages/services/ApplicationFunnelsPage'
import FestivalWebsitesPage from './pages/services/FestivalWebsitesPage'
import WebsitesPage from './pages/services/WebsitesPage'
import DigitalPartnershipPage from './pages/services/DigitalPartnershipPage'

// Platform
import PlatformPage from './pages/platform/PlatformPage'
import DonationsFeaturePage from './pages/platform/features/DonationsFeaturePage'
import WebsiteFeaturePage from './pages/platform/features/WebsiteFeaturePage'
import TeamFeaturePage from './pages/platform/features/TeamFeaturePage'
import EventsFeaturePage from './pages/platform/features/EventsFeaturePage'
import EmailFeaturePage from './pages/platform/features/EmailFeaturePage'
import AnalyticsFeaturePage from './pages/platform/features/AnalyticsFeaturePage'

// Resources
import ResourcesPage from './pages/resources/ResourcesPage'

// Legal
import PrivacyPage from './pages/PrivacyPage'
import AccessibilityPage from './pages/AccessibilityPage'

// 404
import NotFoundPage from './pages/NotFoundPage'

// The Router is provided by the harness, NOT here: mountClient() wraps
// <BrowserRouter> on the client and createRenderBody() wraps <StaticRouter> on
// the server (both from @gjl/site-kit). App must stay router-agnostic so the
// same tree renders in both. injectedData is part of the @gjl/site-kit App
// contract; this marketing site carries no CMS payload, so it is ignored.
function App(_props: { injectedData?: unknown } = {}) {
  return (
    <FeedbackProvider>
      <Routes>
        {/* Standalone pages (no nav) */}
        <Route path="/welcome" element={<WelcomePage />} />
        <Route path="/call" element={<CallPage />} />

        <Route element={<MainLayout />}>
          {/* Main Pages */}
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/get-started" element={<GetStartedPage />} />
          <Route path="/book" element={<BookPage />} />
          <Route path="/who-we-help" element={<WhoWeHelpPage />} />
          <Route path="/how-we-measure" element={<HowWeMeasurePage />} />
          <Route path="/pay" element={<PayPage />} />
          <Route path="/link-in-bio" element={<LinkInBioPage />} />
          <Route path="/checklist" element={<ChecklistPage />} />
          <Route path="/requests" element={<RequestsPage />} />

          {/* Packages */}
          <Route path="/packages" element={<PackagesPage />} />

          {/* Portfolio */}
          <Route path="/portfolio" element={<PortfolioPage />} />
          <Route path="/portfolio/:slug" element={<PortfolioItemPage />} />

          {/* Blog */}
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/chroma-festival-case-study" element={<ChromaFestivalCaseStudyPage />} />
          <Route path="/blog/festivals-losing-applicants" element={<FestivalsLosingApplicantsPage />} />
          <Route path="/blog/sierra-canyon-case-study" element={<SierraCanyonCaseStudyPage />} />
          <Route path="/blog/holy-trinity-case-study" element={<HolyTrinityCaseStudyPage />} />
          <Route path="/blog/brass-city-jazz-case-study" element={<BrassCityJazzCaseStudyPage />} />
          <Route path="/blog/wendell-whalum-case-study" element={<WendellWhalumCaseStudyPage />} />
          <Route path="/blog/best-website-platforms-for-music-festivals" element={<BestWebsitePlatformsPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />

          {/* Services */}
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/services/festival-retainer" element={<FestivalRetainerPage />} />
          <Route path="/services/digital-systems" element={<DigitalSystemsPage />} />
          <Route path="/services/application-funnels" element={<ApplicationFunnelsPage />} />
          <Route path="/services/festival-websites" element={<FestivalWebsitesPage />} />
          <Route path="/services/websites" element={<WebsitesPage />} />
          <Route path="/services/digital-partnership" element={<DigitalPartnershipPage />} />

          {/* Platform */}
          <Route path="/platform" element={<PlatformPage />} />
          <Route path="/platform/features/donations" element={<DonationsFeaturePage />} />
          <Route path="/platform/features/website" element={<WebsiteFeaturePage />} />
          <Route path="/platform/features/team" element={<TeamFeaturePage />} />
          <Route path="/platform/features/events" element={<EventsFeaturePage />} />
          <Route path="/platform/features/email" element={<EmailFeaturePage />} />
          <Route path="/platform/features/analytics" element={<AnalyticsFeaturePage />} />

          {/* Resources */}
          <Route path="/resources" element={<ResourcesPage />} />

          {/* Legal */}
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/accessibility" element={<AccessibilityPage />} />

          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </FeedbackProvider>
  )
}

export default App
